import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { UserPlus, UserMinus, Users } from 'lucide-react';
import { tribalSocialAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const FollowButton = ({ 
  userId, 
  initialFollowStatus = false,
  followType = 'general',
  size = 'default',
  variant = 'default',
  className = '',
  onFollowChange = null
}) => {
  const [isFollowing, setIsFollowing] = useState(initialFollowStatus);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkFollowStatus();
  }, [userId]);

  const checkFollowStatus = async () => {
    if (!userId) return;
    
    try {
      setCheckingStatus(true);
      const status = await tribalSocialAPI.getFollowStatus(userId);
      setIsFollowing(status.is_following);
    } catch (error) {
      console.error('Error checking follow status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!userId || loading) return;
    
    setLoading(true);
    try {
      if (isFollowing) {
        const result = await tribalSocialAPI.unfollowUser(userId);
        if (result.success) {
          setIsFollowing(false);
          toast({
            title: "Unfollowed",
            description: "You are no longer following this user",
          });
          onFollowChange?.(false);
        } else {
          throw new Error(result.message || 'Failed to unfollow');
        }
      } else {
        const result = await tribalSocialAPI.followUser(userId, followType);
        if (result.success) {
          setIsFollowing(true);
          toast({
            title: "Following",
            description: "You are now following this user",
          });
          onFollowChange?.(true);
        } else {
          throw new Error(result.message || 'Failed to follow');
        }
      }
    } catch (error) {
      console.error('Follow/unfollow error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update follow status",
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <Button
        size={size}
        variant="outline"
        disabled
        className={className}
      >
        <Users className="w-4 h-4 mr-2" />
        ...
      </Button>
    );
  }

  return (
    <Button
      size={size}
      variant={isFollowing ? "outline" : variant}
      onClick={handleFollowToggle}
      disabled={loading}
      className={`${className} ${isFollowing ? 'text-gray-600 hover:text-red-600' : ''}`}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
      ) : isFollowing ? (
        <UserMinus className="w-4 h-4 mr-2" />
      ) : (
        <UserPlus className="w-4 h-4 mr-2" />
      )}
      {loading ? 'Processing...' : isFollowing ? 'Unfollow' : 'Follow'}
    </Button>
  );
};

export default FollowButton;